import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import {
  TranslateLoader,
  TranslateService,
  TranslationObject,
  provideTranslateLoader,
} from '@ngx-translate/core';
import { Observable, forkJoin, map, of, shareReplay, tap } from 'rxjs';

const CORE_FEATURES = ['nav', 'common', 'auth', 'reply_card', 'post_actions', 'shared'] as const;

@Injectable()
class TranslateFeatureLoader implements TranslateLoader {
  private http = inject(HttpClient);

  getTranslation(lang: string): Observable<TranslationObject> {
    return forkJoin(
      CORE_FEATURES.map((feature) =>
        this.http.get<TranslationObject>(`/i18n/${lang}/${feature}.json`),
      ),
    ).pipe(map((parts) => Object.assign({}, ...parts) as TranslationObject));
  }
}

export const translateFeatureLoaderProvider = provideTranslateLoader(TranslateFeatureLoader);

@Injectable({ providedIn: 'root' })
class TranslateFeatureService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);
  private loadedFeatures = new Set<string>();
  private inFlight = new Map<string, Observable<TranslationObject>>();

  constructor() {
    this.translate.onLangChange.subscribe(({ lang }) => {
      this.inFlight.clear();
      const features = [...this.loadedFeatures];
      if (features.length === 0) return;
      forkJoin(features.map((feature) => this.fetchAndMerge(feature, lang))).subscribe();
    });
  }

  load(feature: string): Observable<TranslationObject | null> {
    this.loadedFeatures.add(feature);
    const lang = this.translate.currentLang;
    if (!lang) return of(null);
    return this.fetchAndMerge(feature, lang);
  }

  private fetchAndMerge(feature: string, lang: string): Observable<TranslationObject> {
    const key = `${lang}:${feature}`;
    const existing = this.inFlight.get(key);
    if (existing) return existing;
    const req = this.http.get<TranslationObject>(`/i18n/${lang}/${feature}.json`).pipe(
      tap((parts) => this.translate.setTranslation(lang, parts, true)),
      shareReplay(1),
    );
    this.inFlight.set(key, req);
    return req;
  }
}

export function loadFeature(feature: string): ResolveFn<unknown> {
  return () => inject(TranslateFeatureService).load(feature);
}

export function loadFeatures(...features: string[]): ResolveFn<unknown> {
  return () => {
    const svc = inject(TranslateFeatureService);
    return forkJoin(features.map((f) => svc.load(f)));
  };
}
